import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { useThemePalette } from "common/hooks/theme_palette";
import setAuthToken from "services/modules/utils/handel_api";
import {
  getSystemConfigRequest,
  updateSystemConfigRequest,
} from "services/modules/systemConfig";

const EVENTS_KEYS = ["events_packages_enabled", "events_min_lead_days"];

function EventsSettingsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { oliveGreen } = useThemePalette();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const list = useSelector((state) => state.systemConfig.systemConfigs);
  const load = useSelector((state) => state.systemConfig.load);
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) =>
      state.authenticate.currentUserProfile.permissions?.system_configurations,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";
  const canUpdate = isSuperAdmin || permissions?.can_update;

  const [updatingKey, setUpdatingKey] = useState(null);
  const [leadDaysDraft, setLeadDaysDraft] = useState("");

  const eventsConfigs = useMemo(() => {
    const byKey = {};
    list?.forEach((item) => {
      if (EVENTS_KEYS.includes(item?.key)) {
        byKey[item.key] = item;
      }
    });
    return byKey;
  }, [list]);

  const fetchEventsConfigs = () => {
    dispatch(
      getSystemConfigRequest({
        params: {
          page_size: 50,
          page_number: 1,
          keyword: "events",
          sort_by: "id",
        },
      }),
    );
  };

  useEffect(() => {
    fetchEventsConfigs();
  }, []);

  useEffect(() => {
    if (!load?.loading) {
      setUpdatingKey(null);
    }
  }, [load?.loading]);

  useEffect(() => {
    const value = eventsConfigs.events_min_lead_days?.value;
    if (value !== undefined && value !== null) {
      setLeadDaysDraft(String(value));
    }
  }, [eventsConfigs.events_min_lead_days?.value]);

  const isEnabled = (key) => {
    const value = eventsConfigs[key]?.value;
    return value === true || value === "true";
  };

  const handleToggle = (key) => (event) => {
    const config = eventsConfigs[key];
    if (!config?.id || !canUpdate) return;

    const nextValue = event.target.checked ? "true" : "false";
    setUpdatingKey(key);

    dispatch(
      updateSystemConfigRequest({
        id: config.id,
        body: {
          system_configuration: {
            value: nextValue,
          },
        },
        action: () => {
          setUpdatingKey(null);
          toast.success(t("events_settings.updated"));
          fetchEventsConfigs();
        },
      }),
    );
  };

  const handleSaveLeadDays = () => {
    const config = eventsConfigs.events_min_lead_days;
    if (!config?.id || !canUpdate) return;
    setUpdatingKey("events_min_lead_days");
    dispatch(
      updateSystemConfigRequest({
        id: config.id,
        body: {
          system_configuration: {
            value: String(leadDaysDraft),
          },
        },
        action: () => {
          setUpdatingKey(null);
          toast.success(t("events_settings.updated"));
          fetchEventsConfigs();
        },
      }),
    );
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: false,
      title: t("events_settings.title"),
    },
  ];

  return (
    <>
      <Loader open={load?.loading && !updatingKey} />
      <Box>
        <Stack
          direction="row"
          alignSelf="start"
          justifyContent="start"
          gap={2}
          width="100%"
          mb={2}
        >
          <Breadcrumb data={breadcrumbs} />
        </Stack>

        <Typography fontSize="24px" fontWeight="600" mb={3}>
          {t("events_settings.title")}
        </Typography>

        <Stack gap={2} maxWidth="720px">
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #E8E8E8",
              borderRadius: "8px",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Box>
                <Typography fontSize="18px" fontWeight="600" mb={0.5}>
                  {t("events_settings.events_packages_enabled")}
                </Typography>
                <Typography fontSize="14px" color="#797979">
                  {t("events_settings.events_packages_enabled_desc")}
                </Typography>
                {!eventsConfigs.events_packages_enabled?.id && (
                  <Typography fontSize="13px" color="#C62828" mt={1}>
                    {t("events_settings.config_missing")}
                  </Typography>
                )}
              </Box>
              <Switch
                checked={isEnabled("events_packages_enabled")}
                onChange={handleToggle("events_packages_enabled")}
                disabled={
                  !canUpdate ||
                  !eventsConfigs.events_packages_enabled?.id ||
                  updatingKey === "events_packages_enabled"
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: oliveGreen,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: oliveGreen,
                  },
                }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #E8E8E8",
              borderRadius: "8px",
            }}
          >
            <Typography fontSize="18px" fontWeight="600" mb={0.5}>
              {t("events_settings.events_min_lead_days")}
            </Typography>
            <Typography fontSize="14px" color="#797979" mb={2}>
              {t("events_settings.events_min_lead_days_desc")}
            </Typography>
            {!eventsConfigs.events_min_lead_days?.id ? (
              <Typography fontSize="13px" color="#C62828">
                {t("events_settings.config_missing")}
              </Typography>
            ) : (
              <Stack direction="row" gap={2} alignItems="center">
                <TextField
                  type="number"
                  size="small"
                  value={leadDaysDraft}
                  onChange={(e) => setLeadDaysDraft(e.target.value)}
                  disabled={!canUpdate || updatingKey === "events_min_lead_days"}
                  inputProps={{ min: 0 }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveLeadDays}
                  disabled={
                    !canUpdate || updatingKey === "events_min_lead_days"
                  }
                  sx={{ backgroundColor: oliveGreen }}
                >
                  {t("common.save")}
                </Button>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>
    </>
  );
}

export default EventsSettingsPage;
